import { MatchmakingHeader } from './ProDashboardMatchmaking/MatchmakingHeader';
import { MatchmakingSearchInput } from './ProDashboardMatchmaking/MatchmakingSearchInput';
import { MatchmakingEmptyState } from './ProDashboardMatchmaking/MatchmakingEmptyState';
import { MatchmakingNoResultsState } from './ProDashboardMatchmaking/MatchmakingNoResultsState';
import { MatchmakingResultsSection } from './ProDashboardMatchmaking/MatchmakingResultsSection';
import type { HistoryItem } from './MatchmakingHistoryPopover';

type ProDashboardMatchmakingContentProps = {
  clients: Array<{ id: string; profile: { name: string }; avatar: string }>;
  selectedClientId: string | null;
  setSelectedClientId: (id: string | null) => void;
  isDrawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  selectedClient: any;
  clientsLoading: boolean;
  hasClients: boolean;
  matchingPrompt: string;
  onPromptChange: (value: string) => void;
  onGenerateMatches: () => void;
  isLoading: boolean;
  isHistoryPopoverOpen: boolean;
  setHistoryPopoverOpen: (open: boolean) => void;
  showHistoryTooltip: boolean;
  setShowHistoryTooltip: (show: boolean) => void;
  onSelectHistory: (item: HistoryItem) => void;
  advisorId: string | null;
  clientId: string | null;
  shouldFetchHistory: boolean;
  onFetchHistoryComplete: () => void;
  clientsError: any;
  error: string | null;
  showNoProvidersFound: boolean;
  noProviderMessage: string | null;
  hasMatches: boolean;
  isPreloadingImages: boolean;
  currentLoadingTheme: { text: string; color: string };
  currentLoadingMessageIndex: number;
  currentImagePreloadingTheme: { text: string; color: string };
  imagePreloadingMessageIndex: number;
  matchResults: any[];
  preloadedImages: Record<string, string | null>;
  currentSearchId: number | null;
  isViewingHistoryResults: boolean;
  onHistoryDetailsClick: () => void;
  cheerfulMessage: string;
  shouldShowPostSingleLoadExhaustedMessage: boolean;
  shouldShowPostSecondLoadMessage: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreMessage: string | null;
  onLoadMore: () => void;
  onContactUs: () => void;
  onFindProviders: () => void;
};

export const ProDashboardMatchmakingContent = ({
  clients,
  selectedClientId,
  setSelectedClientId,
  isDrawerOpen,
  setDrawerOpen,
  selectedClient,
  clientsLoading,
  hasClients,
  matchingPrompt,
  onPromptChange,
  onGenerateMatches,
  isLoading,
  isHistoryPopoverOpen,
  setHistoryPopoverOpen,
  showHistoryTooltip,
  setShowHistoryTooltip,
  onSelectHistory,
  advisorId,
  clientId,
  shouldFetchHistory,
  onFetchHistoryComplete,
  clientsError,
  error,
  showNoProvidersFound,
  noProviderMessage,
  hasMatches,
  isPreloadingImages,
  currentLoadingTheme,
  currentLoadingMessageIndex,
  currentImagePreloadingTheme,
  imagePreloadingMessageIndex,
  matchResults,
  preloadedImages,
  currentSearchId,
  isViewingHistoryResults,
  onHistoryDetailsClick,
  cheerfulMessage,
  shouldShowPostSingleLoadExhaustedMessage,
  shouldShowPostSecondLoadMessage,
  hasMore,
  isLoadingMore,
    loadMoreMessage,
    onLoadMore,
    onContactUs,
    onFindProviders: _onFindProviders,
}: ProDashboardMatchmakingContentProps) => {
  return (
    <main className="w-full space-y-8 flex-1 min-w-0">
      <MatchmakingHeader
        clients={clients.map(({ id, profile, avatar }) => ({
          id,
          name: profile.name,
          avatar,
        }))}
        selectedClientId={selectedClientId}
        onSelectClient={setSelectedClientId}
        isDrawerOpen={isDrawerOpen}
        setDrawerOpen={setDrawerOpen}
        selectedClient={selectedClient}
        clientsLoading={clientsLoading}
        hasClients={hasClients}
      />

      {/* Help Text - Feature 039 */}
      <p className="text-gray-600">
        Search for service providers on behalf of a client. Tell us about the situation you are trying to address, and the bQuest Navigator tool will return the best providers to address your client's needs.
      </p>
      <p className="text-gray-600">
        To search for providers without a client context, or for a client who isn't in the bQuest application, use the Generic Search tab above.
      </p>

      <MatchmakingSearchInput
        matchingPrompt={matchingPrompt}
        onPromptChange={onPromptChange}
        onGenerateMatches={onGenerateMatches}
        isLoading={isLoading}
        hasClients={hasClients}
        isHistoryPopoverOpen={isHistoryPopoverOpen}
        setHistoryPopoverOpen={setHistoryPopoverOpen}
        showHistoryTooltip={showHistoryTooltip}
        setShowHistoryTooltip={setShowHistoryTooltip}
        onSelectHistory={onSelectHistory}
        advisorId={advisorId}
        clientId={clientId}
        shouldFetchHistory={shouldFetchHistory && !!advisorId}
        onFetchHistoryComplete={onFetchHistoryComplete}
      />

      {!clientsError && clientsLoading && (
        <div className="rounded-2xl border border-[#CFE5D9] bg-[#F5FBF9] px-4 py-3 font-termina text-sm text-[#1F2232]">
          Loading clients...
        </div>
      )}

      {!isLoading && !hasMatches && !error && !showNoProvidersFound && (
        <MatchmakingEmptyState />
      )}

      {showNoProvidersFound && noProviderMessage && (
        <MatchmakingNoResultsState />
      )}

      <MatchmakingResultsSection
        isLoading={isLoading}
        hasMatches={hasMatches}
        showNoProvidersFound={showNoProvidersFound}
        isPreloadingImages={isPreloadingImages}
        currentLoadingTheme={currentLoadingTheme}
        currentLoadingMessageIndex={currentLoadingMessageIndex}
        currentImagePreloadingTheme={currentImagePreloadingTheme}
        imagePreloadingMessageIndex={imagePreloadingMessageIndex}
        matchResults={matchResults}
        preloadedImages={preloadedImages}
        currentSearchId={currentSearchId}
        advisorId={advisorId}
        clientId={clientId}
        isViewingHistoryResults={isViewingHistoryResults}
        onHistoryDetailsClick={onHistoryDetailsClick}
        cheerfulMessage={cheerfulMessage}
        shouldShowPostSingleLoadExhaustedMessage={shouldShowPostSingleLoadExhaustedMessage}
        shouldShowPostSecondLoadMessage={shouldShowPostSecondLoadMessage}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        loadMoreMessage={loadMoreMessage}
        onLoadMore={onLoadMore}
        onContactUs={onContactUs}
      />
    </main>
  );
};

